// OnshapePanel — Core running AS the Onshape right-side panel (Option C,
// Spike_OnshapePanelMode_Brief.md). Rendered by App.jsx INSTEAD of the whole
// app when the URL carries `?panel=1` (Onshape's own Action URL context —
// `documentId`/`workspaceId`/`elementId` come from the URL, not an API call).
// Onshape's panel is NARROW (~300-400px) — single column, no tree, no banner.
//
// HARD RULE (unchanged): no Onshape API call without a user action that knows
// the cost. This whole path is DB-only: the ids come from the URL, the by-ref
// lookup and Register POST are both pure DB reads/writes, and the element read
// stays behind Tag Audit's one button, which states its cost.

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import CascadeSelect, { useCascadeSignal } from './components/CascadeSelect.jsx';
import DbIdBadge from './components/DbIdBadge.jsx';
import OnshapeCompsBlock from './components/OnshapeCompsBlock.jsx';
import OnshapeSection from './components/OnshapeSection.jsx';
import Card from './components/Card.jsx';
import { useAuth } from './AuthGate.jsx';
import { setWorkingProject } from './working-project.js';
import { normalizeOnshapeRef, isValidOnshapeRef } from '../onshape-ref.js';
import { onshapeDocUrl } from './OnshapeLink.jsx';
import { dbHrefFor } from './nav-link.js';
import Text from './components/Text.jsx';
import Notice from './components/Notice.jsx';
import ConfirmPanel from './components/ConfirmPanel.jsx';
import SelectedPartCard from './components/SelectedPartCard.jsx';
import { useOnshapeSelection } from './onshape-selection.js';
import { actionUrlProblems } from './onshape-panel-diagnosis.js';
import OnshapeTagAudit from './components/OnshapeTagAudit.jsx';
import OnshapeSyncReview from './components/OnshapeSyncReview.jsx';

import { NAVY, PANE_GAP, CARD_RADIUS, BTN_PRIMARY, BTN_SECONDARY, BTN_DISABLED, BTN_DESTRUCTIVE_OUTLINE } from './theme-tokens.js';
import { INOVIU_MARK_WHITE } from './inoviu-mark.js';
import { CATALOG, PRODUCT, CRUMB_SEP, LINK } from './icons.js';

const inputStyle = {
  width: '100%', boxSizing: 'border-box', fontSize: 'var(--fs-small)',
  padding: '4px 6px', border: '1px solid var(--th-border-subtle)', borderRadius: 3,
};

// The app has no `--th-link` token; NAVY is what every other in-app link uses
// (OnshapeSection.jsx:364/371). Weight and underline, not colour, carry the
// affordance - Alex is colour-blind, so a link must read as one without the hue.
const LINK_STYLE = { color: NAVY, fontWeight: 600, textDecoration: 'underline' };

// RegisterForm — not-linked flow (Spike_OnshapePanelRegister_Brief.md). Most
// things being registered ALREADY EXIST in Core ("all valves are already
// linked to DB and are categories"), so the primary path is LINK an existing
// item, not create one. Mirrors the component editor's pick-path idiom
// (ProductsView.jsx useCompEditor/SourceEditor/ComponentRefEditor — ▣ Catalog /
// ⊞ Product) as ONE cascade: Source ▸ L1 ▸ L2 ▸ L3 ▸ item, each pick
// auto-opening the next control (the SAME openTick/openNext/sig/advance
// guided-auto-open trio as useCompEditor — see ProductsView.jsx ~line 403-412 /
// ~655 — not a re-invented mechanism):
//   Source=Catalog  → cascade /api/catalog/tree  L1▸L2▸L3▸family (isFamily/_famId)
//                     → Link → PATCH /api/catalog/families/:id { onshape_ref }
//   Source=Product  → cascade /api/products/tree L1▸L2▸L3▸product (isProduct/_productId)
//                     → Link → PATCH /api/products/:id { onshape_ref }
// Create new is a FIRST-CLASS peer of Link existing (Alex, 2026-07-15
// correction — not a hidden fallback), offered once L1▸L2▸L3 is picked,
// distinguished from Link existing by label/position only (Alex is
// color-blind): Product → POST /api/products (unchanged); Catalog → POST
// /api/catalog/families (route + catalog-db.js createFamily), which mints the
// family AND its first `size=''` line in one transaction.
//
// ⚠ That last part was NOT always true, and the old behaviour was a trap: this
// flow used to create a BARE family and leave "catalog_lines are still added
// from the Catalog screen" as the handoff. But listCatalogTree builds its nodes
// from LINES, so a 0-line family rendered NOWHERE — you could never reach it to
// add those lines. Alex hit it registering "Roller Bed" (fam:1051, 2026-07-21).
// Registering here now produces something you can actually see and edit; add
// further SIZES from the Catalog screen as before.
//
// Zero Onshape calls anywhere — both trees and every write here are pure DB
// reads/writes; the ref comes from the URL.
// On success, `onRegistered` re-runs the by-ref lookup so the panel flips to
// the linked view (no local "success" state of its own).
// `mode` (2026-08-21, ON-43 c): 'register' when the element carries no link yet,
// 'change' when it is linked to the WRONG row and Alex is re-pointing it. Same
// cascade, same PATCH - the only difference is what the words admit to. A change
// NEVER passes through an unlinked state: one PATCH moves the ref, so walking away
// mid-edit leaves the old link standing rather than leaving the element orphaned.
// (Unlink-then-link would have been fewer lines and is what the panel forced until
// today; it is also how an interrupted correction turns one wrong link into no
// link, which is strictly worse - a wrong link still shows up in a sync, an absent
// one is silent.)
function RegisterForm({ docRef, onRegistered, mode = 'register', currentLabel = null, onCancel = null }) {
  const [source, setSource] = useState('line'); // 'line' = Catalog (▣), 'product' = Product (⊞)
  const [catalogTree, setCatalogTree] = useState([]);
  const [productTree, setProductTree] = useState([]);
  const [l1, setL1] = useState(null);
  const [l2, setL2] = useState(null);
  const [l3, setL3] = useState(null);
  const [famId, setFamId] = useState(null);       // Catalog leaf pick (_famId)
  const [productId, setProductId] = useState(null); // Product leaf pick (_productId)
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // WHO A ROW CREATED HERE BELONGS TO (PROJECTS_AND_TEMPLATES.md build step 2).
  // The panel renders no top bar, so it never handed working-project.js a value
  // and every "Create new" landed as Standard — a job's own fence included. Now the
  // FIRST pick of the cascade below (Project ▸ Source ▸ …), with the same remembered
  // choice as the app's top-bar Working in
  // (localStorage 'inoviu:activeProjectId'), handed over DURING RENDER exactly as
  // App.jsx does, so the POST below carries it.
  const { group: authGroup, projectId: loginProjectId } = useAuth();
  const lockedProjectId = authGroup === 'project' && loginProjectId ? String(loginProjectId) : null;
  const [activeProjectId, setActiveProjectId] = useState(() => {
    try { return localStorage.getItem('inoviu:activeProjectId') || ''; } catch { return ''; }
  });
  const [projects, setProjects] = useState([]);
  const workingIn = lockedProjectId ?? activeProjectId;
  setWorkingProject(workingIn);
  const chooseWorkingIn = (id) => {
    setActiveProjectId(id);
    try { localStorage.setItem('inoviu:activeProjectId', id); } catch { /* storage blocked: still applies to this panel */ }
  };

  useEffect(() => {
    fetch('/api/projects').then(r => r.json()).then(rows => setProjects(Array.isArray(rows) ? rows : [])).catch(() => {});
  }, []);
  // Refetched per project (D9): the server narrows every read to Standard + the
  // working project, so a tree fetched under one project lacks the next one's rows.
  useEffect(() => {
    fetch('/api/catalog/tree')
      .then(r => r.json())
      .then(t => setCatalogTree(Array.isArray(t) ? t : []))
      .catch(() => setCatalogTree([]));
    fetch('/api/products/tree')
      .then(r => r.json())
      .then(t => setProductTree(Array.isArray(t) ? t : []))
      .catch(() => setProductTree([]));
  }, [workingIn]);

  const isCatalog = source === 'line';
  const tree = isCatalog ? catalogTree : productTree;
  const leafFlagKey = isCatalog ? 'isFamily' : 'isProduct';
  const leafIdKey = isCatalog ? '_famId' : '_productId';
  const pickedLeafId = isCatalog ? famId : productId;

  const l1Node = l1 != null ? tree.find(n => n._catId === l1) : null;
  const l2Opts = l1Node ? (l1Node.children || []).filter(c => !c[leafFlagKey]) : [];
  const l2Node = l2 != null ? l2Opts.find(n => n._catId === l2) : null;
  const l3Opts = l2Node ? (l2Node.children || []).filter(c => !c[leafFlagKey]) : [];
  const l3Node = l3 != null ? l3Opts.find(n => n._catId === l3) : null;
  // Standard + the chosen project only: another job's own rows are never offered,
  // so an element cannot be linked to them by mistake.
  const visibleHere = (n) => n._ownerProjectId == null || String(n._ownerProjectId) === String(workingIn);
  const leafOpts = l3Node ? (l3Node.children || []).filter(c => c[leafFlagKey] && visibleHere(c)) : [];
  const selectedLeaf = pickedLeafId != null ? leafOpts.find(n => n[leafIdKey] === pickedLeafId) : null;

  const toCatOptions = opts => opts.map(n => ({ code: n._catId, label: n.label || '', display: n.code || '' }));
  const toLeafOptions = opts => opts.map(n => ({ code: n[leafIdKey], label: n.label || '', display: n.code || '' }));

  // Guided auto-open (S3g, verbatim mechanism from ProductsView.jsx's
  // useCompEditor) — a pick at one level bumps `openTick` to the next
  // control's key; each CascadeDropdown below is handed `sig(key)` as its
  // `openSignal`, so only the matching control opens (once per bump). Flows
  // Source→L1→L2→L3→item without re-clicking.
  // 2026-08-04: was a verbatim copy of ProductsView's plumbing (the comment above
  // said so); both now call the shared hook.
  const _cascade = useCascadeSignal();
  const openNext = _cascade.advance;
  const sig = _cascade.signalFor;
  const advance = (step) => {
    const next = step === 'project' ? 'source'
      : step === 'source' ? 'l1'
      : step === 'l1' ? 'l2'
      : step === 'l2' ? 'l3'
      : step === 'l3' ? 'item'
      : null;
    if (next) openNext(next);
  };

  const changeSource = (v) => {
    setSource(v); setL1(null); setL2(null); setL3(null); setFamId(null); setProductId(null); setError(null);
    advance('source');
  };

  // PROJECT is the first pick (Alex, 2026-10-12): it is the context for the whole
  // panel. It filters the family/product list below and owns what Create new makes.
  // '' (Standard) travels as STD because a cascade option needs a non-empty code.
  const STD = 'std';
  const changeProject = (v) => {
    chooseWorkingIn(v === STD ? '' : String(v));
    setFamId(null); setProductId(null); setError(null);
    // The category path stays (it does not depend on the owner); open the next list
    // that needs a pick: the leaf when L3 is already chosen, else Source.
    if (l3 != null) openNext('item'); else advance('project');
  };
  const levels = [
    {
      ariaLabel: 'Project', width: 110, allowClear: false,
      disabled: !!lockedProjectId,
      value: workingIn ? workingIn : STD,
      options: [
        ...(lockedProjectId ? [] : [{ code: STD, display: 'Standard', label: 'Standard — visible to every project' }]),
        ...projects
          .filter(p => !lockedProjectId || String(p.id) === lockedProjectId)
          .map(p => ({ code: String(p.id), display: p.code, label: p.customer ? `${p.code} · ${p.customer}` : p.code })),
      ],
      onChange: changeProject,
    },
    {
      ariaLabel: 'Source', width: 60, allowClear: false, openSignal: sig('source'),
      value: source,
      options: [
        { code: 'line', display: CATALOG, label: 'Catalog' },
        { code: 'product', display: PRODUCT, label: 'Product' },
      ],
      onChange: changeSource,
    },
    {
      ariaLabel: 'Level 1 category', width: 100, allowClear: false, placeholder: '— L1 —',
      value: l1, openSignal: sig('l1'), options: toCatOptions(tree),
      onChange: v => {
        const id = v ? Number(v) : null;
        setL1(id); setL2(null); setL3(null); setFamId(null); setProductId(null);
        if (id) advance('l1');
      },
    },
    {
      ariaLabel: 'Level 2 category', width: 100, allowClear: false, placeholder: '— L2 —',
      disabled: !l1Node, value: l2, openSignal: sig('l2'), options: toCatOptions(l2Opts),
      onChange: v => {
        const id = v ? Number(v) : null;
        setL2(id); setL3(null); setFamId(null); setProductId(null);
        if (id) advance('l2');
      },
    },
    {
      ariaLabel: 'Level 3 category', width: 100, allowClear: false, placeholder: '— L3 —',
      disabled: !l2Node, value: l3, openSignal: sig('l3'), options: toCatOptions(l3Opts),
      onChange: v => {
        const id = v ? Number(v) : null;
        setL3(id); setFamId(null); setProductId(null);
        if (id) advance('l3');
      },
    },
    {
      ariaLabel: isCatalog ? 'Family' : 'Product', width: 160, allowClear: false,
      placeholder: isCatalog ? '— family —' : '— product —',
      disabled: !l3Node, value: pickedLeafId, openSignal: sig('item'), options: toLeafOptions(leafOpts),
      onChange: v => {
        const id = v ? Number(v) : null;
        if (isCatalog) setFamId(id); else setProductId(id);
        if (id) advance('item');
      },
    },
  ];

  // The picked leaf's tree coordinate is L3 + its own id - the same two facts that
  // /api/onshape/by-ref joins server-side, free here because the cascade just walked
  // through L3. dbHrefFor picks `node` vs `prod`; building that query by hand is what
  // silently broke every product link (see nav-link.js).
  const leafHref = (l3 != null && pickedLeafId != null)
    ? dbHrefFor(isCatalog ? 'catalog' : 'products',
                isCatalog ? `c3-${l3}|fam-${pickedLeafId}` : `c3-${l3}|${pickedLeafId}`)
    : null;

  const canLink = pickedLeafId != null && !saving;
  const link = () => {
    setSaving(true);
    setError(null);
    const url = isCatalog ? `/api/catalog/families/${pickedLeafId}` : `/api/products/${pickedLeafId}`;
    fetch(url, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ onshape_ref: docRef }),
    })
      .then(res => res.json().then(b => { if (!res.ok) throw new Error(b.error || `HTTP ${res.status}`); return b; }))
      .then(() => onRegistered())
      .catch(err => { setError(err.message); setSaving(false); });
  };

  // Create new — a FIRST-CLASS peer of Link existing (Alex, 2026-07-15
  // correction), not a hidden fallback: as soon as L1▸L2▸L3 is picked, the user
  // can either link something that already exists (above) OR create a fresh
  // entry right there, whichever fits. Catalog creates a family WITH its first
  // size-less line (POST /api/catalog/families — a family with no lines is
  // invisible in the tree, see the header note); Product creates a full product
  // (existing POST /api/products).
  const canCreate = l3 != null && code.trim() && label.trim() && !saving;
  const createEntry = () => {
    setSaving(true);
    setError(null);
    const url = isCatalog ? '/api/catalog/families' : '/api/products';
    const body = isCatalog
      ? { category_id: l3, l4: code.trim(), label: label.trim(), onshape_ref: docRef }
      : { category_id: l3, code: code.trim(), label: label.trim(), onshape_ref: docRef };
    fetch(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(res => res.json().then(b => { if (!res.ok) throw new Error(b.error || `HTTP ${res.status}`); return b; }))
      .then(() => onRegistered())
      .catch(err => { setError(err.message); setSaving(false); });
  };

  const showCreateSection = l3 != null;
  const changing = mode === 'change';

  return (
    <div>
      <div style={{ color: 'var(--th-text-hint)', fontSize: 'var(--fs-body)', marginBottom: 10 }}>
        {changing
          ? <>Pick what this element should be instead{currentLabel ? <> — it is <b>{currentLabel}</b> today</> : null}. The old link is replaced in one step.</>
          : <>Not linked in Core yet — link it to an existing item, or create a new one.</>}
      </div>

      <div style={{ fontWeight: 700, fontSize: 'var(--fs-small)', color: 'var(--th-text-body)', marginBottom: 4 }}>
        Link existing — Project {CRUMB_SEP} Source {CRUMB_SEP} L1 {CRUMB_SEP} L2 {CRUMB_SEP} L3 {CRUMB_SEP} {isCatalog ? 'family' : 'product'}
      </div>
      <div style={{ marginBottom: 8 }}>
        <CascadeSelect levels={levels} gap={4} />
      </div>

      {/* ON-43 (a), 2026-08-21 - THE WARNING NAMES A PLACE YOU CAN GO.
          This used to print the conflicting `did/wid/eid` as 96 characters of raw hex
          and stop there: it said something was wrong and left the only route out of
          Onshape and through the DB, to work out which element that even is. Alex:
          "I should be able to link to find out what I messed up with."
          The repo already had the rule - never name a row without linking it
          (CLAUDE.md, 2026-08-19); it was written about MY answers and applies verbatim
          to the app's own text. Both halves were already owned: `onshapeDocUrl` turns
          the ref into a document URL, and the leaf's tree coordinate is L3 + its id.
          Both open in a NEW TAB for the same reason `dbLinkNewTab` exists below - this
          panel is an iframe inside Onshape, so a same-tab jump would load the target
          inside Onshape's frame instead of opening it.
          The raw ref is not deleted, only demoted to the Notice's `title` tooltip: it
          is still the thing to paste when something needs comparing by hand. */}
      {selectedLeaf && selectedLeaf.onshape_ref && selectedLeaf.onshape_ref !== docRef && (
        <div style={{ marginBottom: 8 }}>
          <Notice kind="warning" title={selectedLeaf.onshape_ref}>
            <span>
              {selectedLeaf.label || selectedLeaf.code} is already linked to a{' '}
              <b>different</b> Onshape element. Linking here overwrites that link.
              <span style={{ display: 'flex', marginTop: 4, gap: 10, flexWrap: 'wrap' }}>
                {onshapeDocUrl(selectedLeaf.onshape_ref) && (
                  <a href={onshapeDocUrl(selectedLeaf.onshape_ref)} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>
                    Open that element in Onshape {LINK}
                  </a>
                )}
                {leafHref && (
                  <a href={leafHref} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>
                    Open {isCatalog ? 'the family' : 'the product'} in Core {LINK}
                  </a>
                )}
              </span>
            </span>
          </Notice>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={link}
          disabled={!canLink}
          style={{ ...BTN_PRIMARY, flex: 1, textAlign: 'center', ...(!canLink ? BTN_DISABLED : null) }}
        >
          {saving ? (changing ? 'Changing…' : 'Linking…') : (changing ? 'Change link' : 'Link')}
        </button>
        {onCancel && (
          <button onClick={onCancel} disabled={saving} style={BTN_SECONDARY}>Cancel</button>
        )}
      </div>

      {showCreateSection && (
        <>
          <div style={{
            borderTop: '1px solid var(--th-border-subtle)', margin: '14px 0 10px', paddingTop: 10,
            fontWeight: 700, fontSize: 'var(--fs-small)', color: 'var(--th-text-body)',
          }}>
            Create new — nothing existing fits this element
          </div>
          <Text kind="note" as="label" style={{ display: 'block', marginBottom: 8 }}>
            {isCatalog ? 'Tag' : 'Code'}
            <input value={code} onChange={e => setCode(e.target.value)} style={{ ...inputStyle, marginTop: 2, fontFamily: 'var(--ff-mono)' }} />
          </Text>
          <Text kind="note" as="label" style={{ display: 'block', marginBottom: 10 }}>
            Label
            <input value={label} onChange={e => setLabel(e.target.value)} style={{ ...inputStyle, marginTop: 2 }} />
          </Text>
          <button
            onClick={createEntry}
            disabled={!canCreate}
            style={{ ...BTN_SECONDARY, display: 'block', width: '100%', textAlign: 'center', ...(!canCreate ? BTN_DISABLED : null) }}
          >
            {saving ? 'Creating…' : (isCatalog ? 'Create new family' : 'Create new product')}
          </button>
        </>
      )}

      {error && (
        <Text kind="deprecated" as="div" style={{ marginTop: 8 }}>⚠ {error}</Text>
      )}
    </div>
  );
}

export default function OnshapePanel() {
  // Ids from the URL ONLY — Onshape's Action URL injects them as query params
  // (`{$documentId}` etc — the `{!}` typo makes Onshape pass a literal string
  // instead, a documented gotcha). Computed once from the initial URL; this
  // panel never navigates, so re-parsing on every render would be wasted work.
  const urlIds = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      documentId: p.get('documentId') || '',
      workspaceId: p.get('workspaceId') || '',
      elementId: p.get('elementId') || '',
      // `{$configuration}` — optional, and only meaningful for a configured
      // element. Disambiguates when ONE element hosts several families (element
      // 6244dd4b… is both fam#35 Flange-CS `Type=SO` and fam#43 Flange-SS304
      // `Type=LJ`): without it the server returns whichever it finds first.
      configuration: p.get('configuration') || '',
    };
  }, []);

  // PART-LEVEL tokens, for the bottom card (ON-42, Alex's two-area vision). The
  // SAME Action-URL invocation carries them: a "Selected part" registration
  // substitutes documentId/workspaceId/elementId AND partNumber/partId, so one
  // panel can answer both questions with no second iframe and no API call.
  //
  // ⚠ `rawPartNumber` keeps the UNFILTERED value on purpose. `sub()` maps both
  // "param absent" and "Onshape had nothing to substitute" to null, but those are
  // different faults with different fixes — one is the registration, one is just
  // nothing selected — and the card must not send Alex looking in the wrong place.
  const partTokens = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    // A token Onshape could not fill arrives as the literal "{$name}".
    const sub = (v) => { const t = String(v ?? '').trim(); return !t || /^[{(]\$\w+[)}]$/.test(t) ? '' : t; };
    const rawPartNumber = p.get('partNumber');
    return {
      rawPartNumber,
      partNumber: sub(rawPartNumber),
      partId: sub(p.get('partId')),
      tabElementId: sub(p.get('tabElementId')),
      documentId: p.get('documentId') || '',
      elementId: p.get('elementId') || '',
      // Cut at '&': a configuration is k=v;k=v and never contains one, so anything
      // past it bled in from a parameter placed after {$configuration}.
      configuration: (sub(p.get('configuration')) || '').split('&')[0],
    };
  }, []);

  // THE LIVE HALF of the same card (ON-42, 2026-09-24). The tokens above are
  // frozen at iframe load; in the document registration that means literals
  // forever. Onshape's SELECTION postMessage follows the clicks instead — zero
  // API calls. Additive: until a readable SELECTION arrives, the card runs on
  // partTokens exactly as before. `server` is Onshape's own appended param and
  // decides which origin the panel trusts.
  const onshapeServer = useMemo(() => new URLSearchParams(window.location.search).get('server') || '', []);
  const liveSelection = useOnshapeSelection(urlIds, onshapeServer);

  // Fallback when the URL has no (or a malformed) document context: let the
  // user paste the Onshape document URL and parse did/wid/eid out of it
  // client-side (normalizeOnshapeRef, same helper the family/product Onshape
  // panels use for a pasted URL) — still zero Onshape calls.
  const [pastedUrl, setPastedUrl] = useState('');
  const pastedRef = pastedUrl ? normalizeOnshapeRef(pastedUrl) : '';

  const docRef = (urlIds.documentId && urlIds.workspaceId && urlIds.elementId)
    ? `${urlIds.documentId}/${urlIds.workspaceId}/${urlIds.elementId}`
    : (isValidOnshapeRef(pastedRef) ? pastedRef : '');

  const [result, setResult] = useState(null); // { kind, item } | null
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // ON-15 — bumped by Refresh; handed to the comps block so its product fetch and
  // its worklist re-read together with this panel's own by-ref lookup.
  const [refreshTick, setRefreshTick] = useState(0);

  const lookup = useCallback(() => {
    if (!docRef) { setResult(null); return; }
    setLoading(true);
    setError(null);
    const q = `ref=${encodeURIComponent(docRef)}`
      + (urlIds.configuration ? `&config=${encodeURIComponent(urlIds.configuration)}` : '');
    fetch(`/api/onshape/by-ref?${q}`)
      .then(res => res.json().then(b => { if (!res.ok) throw new Error(b.error || `HTTP ${res.status}`); return b; }))
      .then(data => { setResult(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [docRef, urlIds.configuration]);

  useEffect(() => { lookup(); }, [lookup]);

  // ON-15 — REFRESH IS A DATABASE READ, AND IT MUST STAY ONE (Alex, 2026-08-16:
  // "Can we add a 'Refresh' button… to avoid refreshing completely Onshape").
  // Everything this panel shows — the by-ref lookup, db_id, the components, the
  // worklist — is already a DB read, so a refresh costs ZERO Onshape calls. If a
  // future block reads Onshape on mount, it must NOT join this path: a refresh
  // button that spends quota is a quota button, and it would be pressed like a
  // refresh button. The BOM block keeps its own Prepare → Load gate for that
  // exact reason and is deliberately not reset here.
  const refresh = useCallback(() => {
    lookup();
    setRefreshTick(t => t + 1);
  }, [lookup]);

  // ON-16 / ON-43 (c), 2026-08-21 - THE MISSING VERB.
  // A link could be CREATED from this panel since 07-15 and never REMOVED: "there is no
  // possibility to remove a link in Onshape to DB without going to the DB and deleting
  // the link." The asymmetry was the tell that the flow was never finished - and
  // re-tagging a document is exactly the job that needs the erase path, because every
  // wrong tag has to come off before the right one goes on.
  //
  // The SERVER half already worked: both PATCH routes send an empty ref through
  // normalizeOnshapeRefOrThrow, documented as "empty clears the link", and store NULL.
  // Nothing new was needed there; the panel simply never offered it.
  //
  // Zero Onshape calls - a PATCH against our own DB, like every other write here.
  const [unlinkAsk, setUnlinkAsk] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [unlinkError, setUnlinkError] = useState(null);
  // 'view' | 'change' - which face the identity card is showing. Reset whenever a
  // fresh lookup lands, so a completed change/unlink returns to the row rather than
  // leaving the cascade open over the answer it just produced.
  const [idMode, setIdMode] = useState('view');
  useEffect(() => { setIdMode('view'); setUnlinkAsk(false); }, [result]);

  // The open Not-synced COUNT, shown on the COMPONENTS title. Same endpoint, same
  // `status === 'open'` filter and the same re-read trigger as ProductsView's own
  // copy - Alex, 2026-08-21: "keep them identical... same must be the same". A
  // second way of counting the same thing is how two screens start disagreeing
  // about one number.
  const [notSyncedCount, setNotSyncedCount] = useState(0);
  useEffect(() => {
    const pid = result?.kind === 'product' ? result.item?.id : null;
    if (!pid) { setNotSyncedCount(0); return; }
    let live = true;
    fetch(`/api/products/${pid}/sync-issues`)
      .then(r => (r.ok ? r.json() : []))
      .then(list => { if (live) setNotSyncedCount(Array.isArray(list) ? list.filter(i => i.status === 'open').length : 0); })
      .catch(() => {});
    return () => { live = false; };
  }, [result, refreshTick]);

  // One name for "the by-ref lookup found a row", used by every card below to pick
  // between its real body and its greyed #NA face.
  const linked = !!result?.kind;

  const unlink = useCallback(() => {
    if (!result?.kind || result.item?.id == null) return;
    setUnlinking(true);
    setUnlinkError(null);
    const url = result.kind === 'product'
      ? `/api/products/${result.item.id}`
      : result.kind === 'category'
        ? `/api/catalog/categories/${result.item.id}`
        : `/api/catalog/families/${result.item.id}`;
    fetch(url, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ onshape_ref: '' }),
    })
      .then(res => res.json().then(b => { if (!res.ok) throw new Error(b.error || `HTTP ${res.status}`); return b; }))
      // No local "unlinked" state: re-running the by-ref lookup is the single source of
      // truth, and it flips the panel to RegisterForm - which is exactly the next thing
      // you want while re-tagging. Unlink and re-link is one continuous motion.
      .then(() => { setUnlinkAsk(false); setUnlinking(false); lookup(); })
      .catch(err => { setUnlinkError(err.message); setUnlinking(false); });
  }, [result, lookup]);

  return (
    <div style={{ height: '100vh', overflowY: 'auto', boxSizing: 'border-box', background: 'var(--th-panel-bg)' }}>
      {/* Same rectangle App.jsx's shell header uses (Alex, 2026-08-20: the two
          "Inoviu" bars — this embedded panel and the app shell — "should both
          behave the same") — margin + radius, navy/white identity kept. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '8px 12px', background: 'var(--th-bar-bg)', color: 'var(--th-bar-text)',
        fontSize: 'var(--fs-small)', fontWeight: 700, letterSpacing: 0.3,
        margin: PANE_GAP, borderRadius: CARD_RADIUS, border: '1px solid var(--th-border-subtle)',
      }}>
        <img src={INOVIU_MARK_WHITE} alt="Inoviu" style={{ height: 12, width: 'auto', filter: 'var(--th-mark-filter)' }} />
        Inoviu
        {/* Refresh sits in the panel's own bar, not inside a section: it refreshes
            the WHOLE panel, and a control's place should say what it acts on. The
            word carries the meaning — `⟳` alone was rejected for icons.js because
            it differs from RESTORE `⟲` only by which way the arrow curls, which is
            exactly the kind of shape-only signal that fails for Alex (colour-blind,
            so meaning must be word or unmistakable shape). With "Refresh" beside
            it the glyph is decoration, not the message. */}
        <button
          onClick={refresh}
          disabled={loading || !docRef}
          title="Re-read the database — no Onshape calls"
          style={{
            marginLeft: 'auto', fontSize: 'var(--fs-small)', fontWeight: 600,
            padding: '2px 8px', borderRadius: 3,
            border: '1px solid var(--th-bar-border)',
            background: 'transparent', color: 'var(--th-bar-text)',
            opacity: (loading || !docRef) ? 0.5 : 1,
            cursor: (loading || !docRef) ? 'default' : 'pointer',
          }}
        >
          {loading ? 'Refreshing…' : '⟳ Refresh'}
        </button>
      </div>
      <div style={{ padding: PANE_GAP }}>
        {!docRef ? (
          <div>
            <div style={{ marginBottom: 6, color: 'var(--th-text-hint)', fontSize: 'var(--fs-body)' }}>
              No document context from the URL — paste the Onshape document URL:
            </div>
            <input
              value={pastedUrl}
              onChange={e => setPastedUrl(e.target.value)}
              placeholder="https://cad.onshape.com/documents/…"
              style={inputStyle}
            />
            {/* WHY there is no context. This is the state the retired Onshape
                context card was mounted outside every branch to explain (a
                missing workspaceId lands here), so its problem list moves here. */}
            {actionUrlProblems().length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 'var(--fs-small)', color: 'var(--th-text-body)' }}>
                {actionUrlProblems().map((p, i) => <li key={i} style={{ marginBottom: 2 }}>{p}</li>)}
              </ul>
            )}
          </div>
        ) : error ? (
          <div style={{ color: 'var(--th-status-deprecated)', fontSize: 'var(--fs-body)' }}>⚠ {error}</div>
        ) : !result ? (
          // `!result`, NOT `loading || !result` — and the difference only shows on a
          // REFRESH. Blanking to "Looking up…" while a result is already on screen
          // unmounts everything below, which (a) reads as "your data is gone" for a
          // read that takes milliseconds and (b) remounts the comps block, so its
          // own refresh effect ran on a fresh mount and fetched the product TWICE.
          // Found by clicking the button and reading the network log, not by review.
          // Progress belongs on the control being pressed — the button says
          // "Refreshing…" — while the content it is replacing stays put until the
          // replacement arrives.
          <div style={{ color: 'var(--th-text-hint)', fontSize: 'var(--fs-body)' }}>Looking up…</div>
        ) : (
          <>
            {/* ONE STACK OF CARDS, ALWAYS THE SAME STACK (2026-08-21, Alex: "don't
                forget the non-applicable states where it's closed and greyed out").
                This branch used to fork the WHOLE panel on linked-ness: linked gave
                you three cards, unlinked gave you a bare un-carded form and the other
                two cards VANISHED. A card that disappears reads as "this does not
                exist here"; a card that is greyed reads as "not yet" — and only the
                second is true. UI_VISION §4's two axes: applicable is a DB fact,
                open/closed is a preference, and absent is neither.

                The identity card is the one that changes FACE rather than presence,
                because it is the same question in three states:
                  linked            → the row it is, plus Change link / Unlink
                  changing          → the cascade, in place, with Cancel
                  not linked        → the cascade, plus Create new
                That is why Register is not a separate screen: it IS this card, in the
                state where identity has not been decided yet. */}
            <Card
              storageKey="onshape-panel:section:identity"
              title={linked ? ({ product: 'Product', family: 'Family', category: 'Category' }[result.kind]) : 'Not linked'}
            >
              {linked && idMode === 'view' ? (
              <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontFamily: 'var(--ff-mono)', fontWeight: 700, fontSize: 'var(--fs-title)', color: 'var(--th-text-body)' }}>
                  {result.item.code}
                </span>
              </div>
              <div style={{ fontSize: 'var(--fs-title)', color: 'var(--th-text-body)' }}>{result.item.label}</div>

              {/* Unlink lives HERE, in the identity card, because identity is what it
                  changes: this card's whole claim is "the element you are looking at IS
                  this row", and the button withdraws that claim. Deliberately not in the
                  Onshape card below, which is about the model, and not in a toolbar,
                  which would float the control away from the thing it acts on
                  (Notice.jsx's rule: the message belongs beside the action). */}
              {unlinkAsk ? (
                <div style={{ marginTop: 8 }}>
                  <ConfirmPanel
                    title="Unlink from Core"
                    question={`Clear the link between this Onshape element and ${result.item.code || result.item.label}?`}
                    impact={[
                      'The Onshape element is not touched - this is a database change only.',
                      ...(result.kind === 'product'
                        ? ['Components already synced from Onshape STAY, and stay editable.',
                           'The "last synced" stamp is cleared - it would otherwise describe a link that no longer exists.']
                        : result.kind === 'category'
                          ? ['Families with no link of their own lose this element too; families with their own link keep it.']
                          : ['The family keeps its sizes, prices and config - only the link goes.']),
                      'You can link this element to something else straight after.',
                    ]}
                    actions={[{ label: unlinking ? 'Unlinking…' : 'Unlink', value: 'go', tone: 'danger' }]}
                    onChoose={v => { if (v === 'go') unlink(); else setUnlinkAsk(false); }}
                  />
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  {/* No Change link on a CATEGORY: the cascade links a family or a
                      product, which would sit beside the category link rather than
                      replace it. A category's link is edited on its catalog node.
                      Change link comes FIRST and is the ordinary-weight control:
                      correcting a wrong link is the common repair, losing the link
                      entirely is the rare one. Unlink keeps the destructive outline
                      so the two never read as interchangeable (Alex is colour-blind —
                      they differ by word and by border weight, not by hue alone). */}
                  {result.kind !== 'category' && (
                  <button onClick={() => { setUnlinkError(null); setIdMode('change'); }} style={BTN_SECONDARY}>
                    Change link
                  </button>
                  )}
                  <button onClick={() => { setUnlinkError(null); setUnlinkAsk(true); }} style={BTN_DESTRUCTIVE_OUTLINE}>
                    Unlink from Core
                  </button>
                </div>
              )}
              {unlinkError && (
                <div style={{ marginTop: 6 }}>
                  <Notice kind="error" onDismiss={() => setUnlinkError(null)}>{unlinkError}</Notice>
                </div>
              )}
              </>
              ) : (
                /* Both remaining faces are the SAME component — the only difference
                   is whether there is a link to replace. */
                <RegisterForm
                  docRef={docRef}
                  onRegistered={lookup}
                  mode={linked ? 'change' : 'register'}
                  currentLabel={linked ? (result.item.label || result.item.code) : null}
                  onCancel={linked ? () => setIdMode('view') : null}
                />
              )}
            </Card>

            {/* THE shared Onshape card (NIGHT_SHIFT.md item 11, 2026-07-21) — now
                with its OWN frame/title (default), the same as every OTHER
                OnshapeSection consumer. `frame={false} + title=""` was never a
                real design choice, only a workaround for there being no card stack
                yet to hold it — same fix OnshapeView.jsx's OnshapeDetailPanel got.
                  linkTarget="db"    no link back to Onshape — we ARE in Onshape
                                     (Alex, 2026-07-16: "The 3D icon in Onshape
                                     still link to Onshape. Useless as we are in
                                     Onshape"). This flag now also suppresses the
                                     OnshapeLink cube, which used to render
                                     unconditionally — without that fix the shared
                                     card would have reinstated exactly the
                                     affordance Alex had removed.
                  dbLinkNewTab       the panel is an IFRAME inside Onshape; a
                                     same-tab jump would load Core inside Onshape's
                                     frame rather than opening the app.
                  showThumbnail/showViewer false — the model is already on screen
                                     next to this panel, and both cost Onshape calls.
                Deep-links via `?view=&node=` (App.jsx, 2026-07-16 — it used to dump
                you at the app root); TreeNav auto-expands to the node, so it lands
                ON the item, not merely in the right bucket. */}
            <OnshapeSection
              onshapeRef={result.item?.onshape_ref}
              density="shrink"
              showThumbnail={false}
              showViewer={false}
              showRef
              linkTarget="db"
              dbLinkNewTab
              dbHref={dbHrefFor(result.view, result.nodeId)}
              storageKey="onshape-panel"
              applicable={linked}
              naReason="This element is not linked to a Core row yet — link it above and its model, DB link and db_id appear here."
            >
              {/* The ids to hand to Onshape (Alex, 2026-07-16). Onshape is READ-ONLY
                  (RULES 10) — nothing here writes. The panel shows what the part
                  SHOULD carry so Alex can paste it into the Onshape property himself.
                  db_id comes from the server (one scheme, not re-derived here);
                  config_id is DERIVED from the family's base + the line's size, which
                  is why it never needs entering in the DB.
                  ⚠ Rides in as `children` rather than OnshapeSection's `dbId` prop —
                  panel mode is a LIST plus a card. When several families share the
                  element every candidate must be offered, and a single badge slot in
                  the card's link row cannot express that. So the paste box stays
                  panel body (same way the catalog family panel passes its link
                  editor); the card keeps the ref and the DB link. */}
              {linked && (
              <div style={{ marginTop: 4, padding: 8, background: 'var(--tg-node-l0-bg)', borderRadius: 4 }}>
                <Text kind="note" as="div" style={{ marginBottom: 4 }}>
                  Paste <code style={{ fontFamily: 'var(--ff-mono)' }}>db_id</code> into the Onshape part
                </Text>

                {/* AMBIGUOUS: several families share this element and their config
                    bases are IDENTICAL, so nothing in Onshape can tell them apart
                    (the two Gasket families, CS vs SS304, carry the same base).
                    Alex hit this and said "Double, and I did not know what to do" —
                    because the panel showed ONE candidate and hid the choice. That
                    is precisely what db_id is for: the human knows which it is, the
                    config doesn't. So list them ALL and let him pick. */}
                {/* CATEGORY (2026-10-13): the candidates are the families that
                    INHERIT this element (no link of their own), narrowed by config.
                    The category itself has no db_id to paste. */}
                {result.kind === 'category' && result.candidates?.length === 0 ? (
                  <Text kind="note" as="div">
                    No family inherits this element: every family under {result.item.code} has its own link.
                  </Text>
                ) : result.candidates && result.candidates.length > 1 ? (
                  <>
                    <Text kind="deprecated" as="div" style={{ marginBottom: 4 }}>
                      {result.kind === 'category'
                        ? <>⚠ {result.candidates.length} families inherit this element from {result.item.code} and the
                          config can't separate them — pick the right one:</>
                        : <>⚠ {result.candidates.length} families share this element and the config can't
                          separate them — pick the right one:</>}
                    </Text>
                    {result.candidates.map(c => (
                      <DbIdBadge key={c.dbId} dbId={c.dbId} label={c.label} mono={false} variant="row" />
                    ))}
                  </>
                ) : (
                  <DbIdBadge dbId={result.kind === 'category' ? result.candidates[0].dbId : result.dbId}
                    label={result.kind === 'category' ? result.candidates[0].label : undefined}
                    mono={result.kind === 'category' ? false : undefined}
                    variant="row" />
                )}

                {/* config_id is NOT pasteable — Alex's FeatureScript stamps it at
                    regen, in the part ("it does not cost API call"). Shown as
                    context only. It also has no single value at FAMILY level: each
                    line (size) resolves its own, base + size axis. */}
                {result.item?.onshape_config && (
                  <div style={{ marginTop: 4, fontSize: 'var(--fs-small)', color: 'var(--th-text-hint)' }}>
                    config_id — your FS writes this ({result.item.onshape_config} + size per line)
                  </div>
                )}
              </div>
              )}

              {/* THE SYNC TRIGGER LIVES HERE, not with the components — because
                  that is where the Product screen keeps it (its `onshape` spot),
                  and Alex's rule for these two surfaces is "same must be the
                  same". It sat in the components block until 2026-08-21 purely
                  because the block had been written as one composite lump. */}
              {linked && result.kind === 'product' && result.item?.onshape_ref && (
                <OnshapeSyncReview
                  productId={result.item.id}
                  onshapeRef={result.item.onshape_ref}
                  syncedAt={result.item.onshape_synced_at}
                  refreshToken={refreshTick}
                  onApplied={lookup}
                  onRead={() => setRefreshTick(t => t + 1)}
                />
              )}
            </OnshapeSection>

            {/* NO BOM CARD IN THE PANEL (2026-10-13, Alex: "Still too many buttons").
                On a family it read the same element as Tag Audit below — Prepare,
                then Load, then Tag Audit's Read: three buttons, two paid reads, one
                answer. Tag Audit is now the element's one read and shows the
                quantity too. On a product the Sync was already the only reader
                (2026-08-19). The BOM itself stays on Core's Onshape screen. */}

            {/* A linked PRODUCT gets the sync trigger + resulting component
                list right here in the panel (UI_VISION §8 step 2) — "I see
                what the other is seeing when I have an Onshape, and I can do
                a better job" (Alex). Same shared block as the Onshape screen's
                right panel; read-only grid, editing stays in Core. Its own
                internal "Not synced" section keeps its own nested title — same
                as it does on the Product screen, one Card holding a composite
                block rather than every internal section becoming a sibling card.
                Titled "Sync", not "Components" — the block's own internal grid
                label already says "Components", and a Card titled the same
                would just repeat it right above itself. */}
            {/* The block now draws its OWN two cards (Components / Not synced),
                matching the Product screen's two sibling spots — so no wrapper
                card here, which would have put a third frame around them. */}
            {result.kind === 'product' && result.item?.id != null && (
              <OnshapeCompsBlock productId={result.item.id} refreshToken={refreshTick} notSyncedCount={notSyncedCount} />
            )}

            {/* THE BOTTOM AREA (Alex, 2026-08-21): everything above answers "what
                did I link this element to"; this answers "what does the part
                actually carry", from the part number, at no API cost. Kept as the
                LAST card because it is the check, and a check reads after the
                thing it checks. */}
            {/* The re-tagging worklist. Sits above Selected part because it is the
                one that works in EVERY context Alex tested — Selected part depends on
                a selection the document registration never has. */}
            {/* ⚠ RETIRED ON A PRODUCT (Alex, 2026-10-11: "only have one button,
                and that everything goes somewhere"). On a linked product the Sync
                now files every part in Components, Not synced or Not applicable,
                so a second reader of the same element is a second button for the
                same answer. Kept for a family or an unlinked element, where there
                is no Sync. Deleting it outright is BACKLOG ON-45. */}
            {!(linked && result.kind === 'product') && (
              <OnshapeTagAudit
                onshapeRef={linked ? result.item?.onshape_ref : null}
                storageKey="onshape-panel:section:tag-audit"
              />
            )}

            <SelectedPartCard
              tokens={partTokens}
              live={liveSelection}
              elementDbId={linked ? result.dbId : null}
              storageKey="onshape-panel:section:selected-part"
            />

          </>
        )}

      </div>
    </div>
  );
}
